import { createAppI18n, sharedTranslations, type TKeyOf } from '@repo/i18n'
import { adminTranslations } from '@/routes/-translations/admin-translations'
import { authTranslations } from '@/routes/(auth)/-translations/auth-translations'
import { adminsTranslations } from '@/routes/(private)/admins/-translations/admins-translations'
import { supportTranslations } from '@/routes/(private)/assistenza/-translations/support-translations'
import { monitoringTranslations } from '@/routes/(private)/bandi/-translations/monitoring-translations'
import { callTranslations } from '@/routes/(private)/bando/-translations/call-translations'
import { dsuQuotaTranslations } from '@/routes/(private)/bando/$residenceId/attivita/quota-dsu/-translations/dsu-quota-translations'
import { applicationsTranslations } from '@/routes/(private)/bando/$residenceId/domande/-translations/applications-translations'
import { rankingTranslations } from '@/routes/(private)/bando/$residenceId/graduatoria/-translations/ranking-translations'
import { reportsTranslations } from '@/routes/(private)/bando/$residenceId/graduatoria/segnalazioni/-translations/reports-translations'
import { arrivalsTranslations } from '@/routes/(private)/bando/$residenceId/ingressi/-translations/arrivals-translations'
import { offersTranslations } from '@/routes/(private)/bando/$residenceId/offerte/-translations/offers-translations'
import { flagsTranslations } from '@/routes/(private)/bando/$residenceId/segnalazioni/-translations/flags-translations'
import { verificationsTranslations } from '@/routes/(private)/bando/$residenceId/verifiche/-translations/verifications-translations'
import { emailTextsTranslations } from '@/routes/(private)/email-texts/-translations/email-texts-translations'
import { exportsTranslations } from '@/routes/(private)/exports/-translations/exports-translations'
import { sendingTranslations } from '@/routes/(private)/invii/-translations/sending-translations'
import { profileTranslations } from '@/routes/(private)/profile/-translations/profile-translations'
import { residencesTranslations } from '@/routes/(private)/residences/-translations/residences-translations'
import { usersTranslations } from '@/routes/(private)/users/-translations/users-translations'
import { commonTranslations } from '@/translations/common-translations'

/**
 * App-owned i18n facade. The engine and the shared `shared` namespace (text
 * built into `@repo/ui` components) live in `@repo/i18n`; the app's own
 * `common` namespace and feature namespaces live here. App code keeps importing
 * from `@/lib/i18n`.
 */
const resources = {
	en: {
		common: commonTranslations.en,
		shared: sharedTranslations.en,
		admin: adminTranslations.en,
		'admin-admins': adminsTranslations.en,
		'admin-applications': applicationsTranslations.en,
		'admin-call': callTranslations.en,
		'admin-email-texts': emailTextsTranslations.en,
		'admin-exports': exportsTranslations.en,
		'admin-flags': flagsTranslations.en,
		'admin-monitoring': monitoringTranslations.en,
		'admin-offers': offersTranslations.en,
		'admin-ranking': rankingTranslations.en,
		'admin-ranking-reports': reportsTranslations.en,
		'admin-residences': residencesTranslations.en,
		'admin-sending': sendingTranslations.en,
		'admin-support': supportTranslations.en,
		'admin-users': usersTranslations.en,
		'admin-verifications': verificationsTranslations.en,
		'admin-arrivals': arrivalsTranslations.en,
		'admin-dsu-quota': dsuQuotaTranslations.en,
		auth: authTranslations.en,
		profile: profileTranslations.en
	},
	it: {
		common: commonTranslations.it,
		shared: sharedTranslations.it,
		admin: adminTranslations.it,
		'admin-admins': adminsTranslations.it,
		'admin-applications': applicationsTranslations.it,
		'admin-call': callTranslations.it,
		'admin-email-texts': emailTextsTranslations.it,
		'admin-exports': exportsTranslations.it,
		'admin-flags': flagsTranslations.it,
		'admin-monitoring': monitoringTranslations.it,
		'admin-offers': offersTranslations.it,
		'admin-ranking': rankingTranslations.it,
		'admin-ranking-reports': reportsTranslations.it,
		'admin-residences': residencesTranslations.it,
		'admin-sending': sendingTranslations.it,
		'admin-support': supportTranslations.it,
		'admin-users': usersTranslations.it,
		'admin-verifications': verificationsTranslations.it,
		'admin-arrivals': arrivalsTranslations.it,
		'admin-dsu-quota': dsuQuotaTranslations.it,
		auth: authTranslations.it,
		profile: profileTranslations.it
	}
} as const

const appI18n = createAppI18n(resources, { defaultNS: 'common' })

export const { useTranslation, Trans, translateApp } = appI18n

/**
 * All translation keys with namespace prefix (e.g.
 * `shared:http_errors.default`).
 */
export type TKey = TKeyOf<(typeof resources)['en']>

const i18n = appI18n.i18n

export default i18n

export {
	APP_LANGUAGES,
	type AppLanguage,
	type AppLanguageCode,
	changeAppLanguage,
	DEFAULT_LANGUAGE,
	detectAppLanguageFromRequest,
	getInitialAppLanguage,
	isAppLanguageCode,
	resolveAppLanguageCode,
	syncAppLanguage,
	translateKey
} from '@repo/i18n'
